import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Target, Eye, Heart, Shield, Users, TrendingUp, Sparkles } from 'lucide-react';
import { missionVisionValues } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const MissionVision = () => {
  const sectionRef = useRef(null);

  const valueIcons = {
    Hygiene: Shield,
    Transparency: Eye,
    Support: Users,
    Growth: TrendingUp
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.mission-card',
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 lg:py-32 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-red-500/20 text-red-400 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Our Foundation
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Mission • Vision • <span className="text-yellow-400">Values</span>
          </h2>
        </div>

        {/* Mission & Vision Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* Mission */}
          <div className="mission-card bg-gradient-to-br from-red-500/10 to-red-600/10 rounded-2xl p-8 border border-red-500/20">
            <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mb-6">
              <Target className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Our Mission
            </h3>
            <p className="text-xl text-gray-300 leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {missionVisionValues.mission}
            </p>
          </div>

          {/* Vision */}
          <div className="mission-card bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 rounded-2xl p-8 border border-yellow-500/20">
            <div className="w-16 h-16 rounded-2xl bg-yellow-500/20 flex items-center justify-center mb-6">
              <Sparkles className="w-8 h-8 text-yellow-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Our Vision
            </h3>
            <p className="text-xl text-gray-300 leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {missionVisionValues.vision}
            </p>
          </div>
        </div>

        {/* Core Values */}
        <div>
          <h3 className="text-2xl font-bold text-white text-center mb-10" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <Heart className="w-6 h-6 inline-block text-red-400 mr-2" />
            Our Core Values
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {missionVisionValues.values.map((value, index) => {
              const IconComponent = valueIcons[value.title];
              return (
                <div 
                  key={index}
                  className="mission-card bg-white/5 rounded-2xl p-6 text-center hover:bg-white/10 transition-all duration-300 border border-white/10"
                >
                  <div className="w-14 h-14 mx-auto rounded-xl bg-white/10 flex items-center justify-center mb-4">
                    {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {value.title}
                  </h4>
                  <p className="text-gray-400 text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionVision;
